import { useEffect, useState } from "react"
import { Button, FontAwesomeIcon, FormField, Link } from "@nef/core"
import { useForm, Controller, set } from "react-hook-form"
import { useDispatch } from "react-redux"

import { authenticateApi } from "../../services/api"
import useAuthContext from "../../hooks/useAuthContext"
import { NFN_LEGACY_HREF, NFN_MFA_HREF } from "../../constants/common"
import { AppDispatch } from "../../app/store"
import { verifyUserForLogin } from "../../redux/auth/actions"
import RecordIsMissingModal from "../../modals/common/RecordIsMissingModal"

const LoginFormComponent = ({ setSideBarMenu }: { setSideBarMenu: Function }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { setAccessToken } = useAuthContext()
  const [isLoading, setLoading] = useState<boolean>(false)
  const [isUserNameValid, setIsUserNameValid] = useState<boolean>(false);
  const [loginUserName, setLoginUserName] = useState<string>("");
  const [loginFailed, setLoginFailed] = useState<boolean>(false)
  const [recordMissingModalProps, setRecordMissingModalProps] = useState<any>({})
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset
  } = useForm({
    mode: "onChange",
    defaultValues: {
      userName: "",
      password: "",
      totp: ""
    }
  })

  useEffect(() => {
    if (isUserNameValid) {
      reset({ userName: loginUserName })
    }
  }, [isUserNameValid])
  
  const closeMissingModal = () => {
    setRecordMissingModalProps({ isOpen: false })
  }

  useEffect(() => {
    if (isUserNameValid) {
      reset({ userName: loginUserName })
    }
  }, [isUserNameValid])

  const onSubmit = (data: any) => {
    setLoading(true)
    setLoginFailed(false)
    authenticateApi
      .apiAuthenticateAuthenticatePost({
        userName: data.userName,
        password: data.password,
        totp: data.totp
      })
      .then((response) => {
        if (response.data.data) {
          // Fetch user details after login and do not allow to login if record is missing
          const parsedToken = JSON.parse(atob(response.data.data.split(".")[1]))
          if (parsedToken.recordIsMissing === "True") {
            setRecordMissingModalProps({ isOpen: true })
            setLoading(false)
            return;
          }

          setAccessToken(response.data.data)
          authenticateApi.setAccessToken(response.data.data)
          reset()
          authenticateApi
            .apiAuthenticateGetRoutesGet()
            .then(({ data: routes }) => {
              setSideBarMenu(routes)
              window.localStorage.setItem("_routemap", JSON.stringify(routes))
            })
            .catch(() => { })
        }
        setLoading(false)
      })
      .catch(() => {
        setLoginFailed(true)
        setLoading(false)
      })
  }

  const onUserVerification = async () => {
    try {
      const response = await dispatch(verifyUserForLogin({ userName: loginUserName })).unwrap()
      if (response === 200) {
        setIsUserNameValid(true)
        setLoginFailed(false)
      }
    }
    catch (err) {
      setLoginFailed(true);
    }
  }

  return (
    <>
      {
        isUserNameValid ?
          <form onSubmit={handleSubmit(onSubmit)} className="login-form">
            <Controller
              name="userName"
              control={control}
              rules={{ required: { value: true, message: "User ID is required." } }}
              render={({ field: { onChange, value, name }, fieldState }) => (
                <FormField
                  disabled={isLoading}
                  placeholder="User ID"
                  data-testid="user_id"
                  name={name}
                  value={value}
                  invalid={fieldState.error != null}
                  feedback={fieldState.error?.message}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{ required: { value: true, message: "Password is required." } }}
              render={({ field: { onChange, value, name }, fieldState }) => (
                <FormField
                  id='password'
                  disabled={isLoading}
                  placeholder="Password"
                  name={name}
                  value={value}
                  onChange={onChange}
                  type="password"
                  data-testid="password"
                  invalid={fieldState.error != null}
                  feedback={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="totp"
              control={control}
              rules={{
                pattern: { value: /^[0-9]{6}$/, message: "MFA Code is 6 digits." },
                required: { value: true, message: "MFA Code is required." }
              }}
              render={({ field: { onChange, value, name }, fieldState }) => (
                <FormField
                  disabled={isLoading}
                  placeholder="MFA Code"
                  name={name}
                  data-testid="mfa_code"
                  value={value}
                  onChange={onChange}
                  invalid={fieldState.error != null}
                  feedback={fieldState.error?.message}
                />
              )}
            />


            <a href={NFN_MFA_HREF}
              target="_blank"
              rel="noreferrer"
              data-testid="mfa_link"
              className="login-form__legacy-link">
              Set up MFA / Forgot Password <FontAwesomeIcon iconClassName="fas fa-external-link-alt" />
            </a>
            {loginFailed && <p>Authentication failed.</p>}
            <Button disabled={isLoading || !isValid} type="submit"
              data-testid="login_btn"
              onClick={handleSubmit(onSubmit)}
              className="login-form__login-btn">
              Login
            </Button>
            <a href={NFN_LEGACY_HREF} target="_blank"
              rel="noreferrer"
              data-testid="legacy_link"
              className="login-form__legacy-link">
              Legacy NFN <FontAwesomeIcon iconClassName="fas fa-external-link-alt" />
            </a>
          </form>
          :
          <div className="login-form">
            <FormField
              disabled={isLoading}
              placeholder="User ID"
              data-testid="user_id"
              value={loginUserName}
              onChange={(e) => { setLoginUserName(e?.target?.value); }}
            // name={name}
            // value={value}
            // invalid={fieldState.error != null}
            // feedback={fieldState.error?.message}
            // onChange={onChange}
            />
            <a href={NFN_MFA_HREF}
              target="_blank"
              rel="noreferrer"
              data-testid="mfa_link"
              className="login-form__legacy-link">
              Set up MFA / Forgot Password <FontAwesomeIcon iconClassName="fas fa-external-link-alt" />
            </a>
            {loginFailed && <p>Authentication failed.</p>}
            <Button disabled={isLoading || !isValid} type="submit"
              data-testid="login_btn"
              onClick={onUserVerification}
              className="login-form__login-btn">
              Login
            </Button>
            <a href={NFN_LEGACY_HREF} target="_blank"
              rel="noreferrer"
              data-testid="legacy_link"
              className="login-form__legacy-link">
              Legacy NFN <FontAwesomeIcon iconClassName="fas fa-external-link-alt" />
            </a>
          </div>
      }
      <RecordIsMissingModal
        closeMissingModal={closeMissingModal}
        {...recordMissingModalProps}
      />
    </>
  )
}

export default LoginFormComponent
